import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  Box,
  Divider,
  Link,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

/**
 * Renders a plain GitHub-flavored Markdown string into MUI components so the
 * in-app docs match the DuoNep theme (brand primary #1B5E7A, compact sizes).
 * Root-relative links (``/docs/...``) navigate inside the SPA; external links
 * open in a new tab.
 */
export default function MarkdownRenderer({ children }) {
  const navigate = useNavigate()

  const components = {
    h1: ({ children: c }) => (
      <Typography variant="h4" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
        {c}
      </Typography>
    ),
    h2: ({ children: c }) => (
      <Typography variant="h5" fontWeight={700} sx={{ mt: 4, mb: 1.5 }}>
        {c}
      </Typography>
    ),
    h3: ({ children: c }) => (
      <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
        {c}
      </Typography>
    ),
    h4: ({ children: c }) => (
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
        {c}
      </Typography>
    ),
    p: ({ children: c }) => (
      <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
        {c}
      </Typography>
    ),
    a: ({ href, children: c }) => {
      const isInternal = href && href.startsWith('/')
      if (isInternal) {
        return (
          <Link
            component="button"
            type="button"
            onClick={() => navigate(href)}
            sx={{ verticalAlign: 'baseline' }}
          >
            {c}
          </Link>
        )
      }
      return (
        <Link href={href} target="_blank" rel="noopener noreferrer">
          {c}
        </Link>
      )
    },
    ul: ({ children: c }) => (
      <List dense sx={{ listStyleType: 'disc', pl: 3, py: 0, mb: 1.5 }}>
        {c}
      </List>
    ),
    ol: ({ children: c }) => (
      <List dense sx={{ listStyleType: 'decimal', pl: 3, py: 0, mb: 1.5 }}>
        {c}
      </List>
    ),
    li: ({ children: c }) => (
      <ListItem sx={{ display: 'list-item', py: 0.25, pl: 0.5 }}>
        <Typography variant="body2" component="span" sx={{ lineHeight: 1.6 }}>
          {c}
        </Typography>
      </ListItem>
    ),
    strong: ({ children: c }) => (
      <Box component="strong" sx={{ fontWeight: 700 }}>
        {c}
      </Box>
    ),
    em: ({ children: c }) => <Box component="em">{c}</Box>,
    code: ({ inline, children: c }) =>
      inline ? (
        <Box
          component="code"
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
          }}
        >
          {c}
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: 2,
            bgcolor: 'grey.900',
            color: 'grey.100',
            overflowX: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
          }}
        >
          <code>{c}</code>
        </Box>
      ),
    blockquote: ({ children: c }) => (
      <Box
        sx={{
          borderLeft: 4,
          borderColor: 'primary.main',
          pl: 2,
          py: 0.5,
          my: 1.5,
          color: 'text.secondary',
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        {c}
      </Box>
    ),
    hr: () => <Divider sx={{ my: 3 }} />,
    table: ({ children: c }) => (
      <TableContainer sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small">{c}</Table>
      </TableContainer>
    ),
    thead: ({ children: c }) => <TableHead>{c}</TableHead>,
    tbody: ({ children: c }) => <TableBody>{c}</TableBody>,
    tr: ({ children: c }) => <TableRow>{c}</TableRow>,
    th: ({ children: c }) => (
      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>{c}</TableCell>
    ),
    td: ({ children: c }) => <TableCell>{c}</TableCell>,
  }

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  )
}
